import React, { useLayoutEffect, useMemo } from 'react';
import { Box, Grid, Preloader } from '@common-fe/common-fe';

import { Document } from '@/common/types';
import { RenderWrapperProps } from '@/components/elements/DocumentViewer/DocumentViewer.constants';
import useConvertPdfToPng from '@/utils/hooks/useConvertPdfToPng';

import DocumentPageWrapper from './DocumentPageWrapper';
import ImageViewer from './ImageViewer';

interface Props {
  receipt?: Document;
  zoom: number;
  rotate: number;
  renderWrapper?: (props: RenderWrapperProps) => React.ReactNode;
  isMultiPageViewMode?: boolean;
  setIsMultiPageViewMode?: (isMultiPageDocument: boolean) => void;
  setIsRotationDisabled?: (value: boolean) => void;
  setIsZoomDisabled?: (value: boolean) => void;
}

const PdfViewer: React.FC<Props> = ({
  receipt,
  zoom,
  rotate,
  renderWrapper,
  isMultiPageViewMode,
  setIsMultiPageViewMode,
  setIsRotationDisabled,
  setIsZoomDisabled,
}) => {
  const url = receipt?.blob;
  const { pngDocuments, isLoading } = useConvertPdfToPng({ url });
  const hasConversionError = url && !pngDocuments.length;
  const currentIsMultiPageViewMode = useMemo(
    () => zoom < 1 && isMultiPageViewMode,
    [zoom, isMultiPageViewMode]
  );
  const currentImageZoom = useMemo(() => (isMultiPageViewMode ? 1 : zoom), [isMultiPageViewMode, zoom]);

  useLayoutEffect(() => {
    if (zoom < 1) {
      setIsMultiPageViewMode?.(true);
    } else {
      setIsMultiPageViewMode?.(false);
    }
  }, [setIsMultiPageViewMode, zoom]);

  useLayoutEffect(() => {
    return () => {
      setIsMultiPageViewMode?.(false);
    };
  }, [setIsMultiPageViewMode, pngDocuments?.length]);

  useLayoutEffect(() => {
    if (hasConversionError) {
      setIsRotationDisabled?.(true);
      setIsZoomDisabled?.(true);
    }

    return () => {
      setIsRotationDisabled?.(false);
      setIsZoomDisabled?.(false);
    };
  }, [hasConversionError, setIsRotationDisabled, setIsZoomDisabled]);

  if (isLoading) {
    return <Preloader testId="pdf-preloader" />;
  }

  return (
    <Grid
      data-testid={`pdf-viewer${currentIsMultiPageViewMode ? '_grid' : ''}`}
      width="inherit"
      height="inherit"
      columns={currentIsMultiPageViewMode ? '1/2' : '1fr'}
      gap={{ column: 'spacing48', row: 'spacing12' }}
    >
      {pngDocuments?.map((pngDocument, index) => {
        const content = currentIsMultiPageViewMode ? (
          <DocumentPageWrapper>
            <ImageViewer
              rotate={rotate}
              index={index}
              fileId={receipt?.fileId}
              zoom={currentImageZoom}
              receipt={pngDocument}
            />
          </DocumentPageWrapper>
        ) : (
          <ImageViewer
            rotate={rotate}
            index={index}
            fileId={receipt?.fileId}
            zoom={zoom}
            receipt={pngDocument}
          />
        );

        return (
          <Box key={index} margin={{ bottom: 'spacing12' }}>
            {renderWrapper
              ? renderWrapper({
                  index,
                  document: pngDocument,
                  content,
                  isMultiPageViewMode: currentIsMultiPageViewMode,
                  setIsMultiPageViewMode,
                })
              : content}
          </Box>
        );
      })}
      {hasConversionError ? (
        <Box width={{ min: '620px', width: 'inherit' }} height={{ min: '500px', height: 'inherit' }}>
          <embed src={url} width="100%" height="100%" />
        </Box>
      ) : null}
    </Grid>
  );
};

export default PdfViewer;
